import {
  BarChart3,
  Calendar,
  Globe,
  Instagram,
  Monitor,
  Settings,
  Sparkles,
} from "lucide-react";
import type { SiteContent } from "./content.types";

export const contentEn: SiteContent = {
  home: {
    hero: {
      title: "Get more bookings with a system that works 24/7",
      subtitle:
        "Instagram, website, online booking and automation working together so your diary fills up without you living on your phone.",
      diagramLabel:
        "Lucentia System diagram: Instagram, website, bookings, automation and optimisation converge on your business and end in a confirmed booking.",
      center: "Your business",
      centerDone: "Diary in motion",
      channels: ["Instagram", "Website", "Bookings", "Automation", "Optimisation"],
      booking: { title: "Booking confirmed", detail: "Saturday · 11:00" },
      byline: { name: "Jenifer Roelstraete", role: "Digital strategy for appointment-based businesses" },
    },
    nichesLabel: "Businesses we work with",
    niches: [
      "Hair salons",
      "Beauty centres",
      "Nail studios",
      "Tattoo and piercing studios",
      "Physiotherapists",
      "Nutritionists",
      "Clinics",
      "Photographers",
      "Dog groomers",
    ],
    problem: {
      title: "You post, you answer messages all day, and the diary still isn't full.",
      lines: [
        "You post on Instagram, but without a strategy behind it.",
        "Your website looks good, but it doesn't bring in bookings.",
        "And you spend the day answering WhatsApp instead of looking after clients.",
      ],
      conclusion: "It isn't a lack of effort. It's a lack of system.",
    },
    system: {
      title: "Five pieces, one system",
      subtitle:
        "The Lucentia System® connects every part of your digital presence so they work together: attract, convert, book, automate and improve.",
      stepLabel: "Piece",
      cta: "Discover the Lucentia System®",
    },
    services: {
      title: "Choose where to start",
      subtitle: "Not every business needs the same thing. Each service can be booked on its own or combined within the system.",
      fromLabel: "from",
      addOnsTitle: "And if you need it",
    },
    plans: {
      title: "Clear plans, no small print",
      subtitle: "Each plan adds more content and more support. The rays show each month's posts.",
      postsLabel: "posts a month",
      foundationLabel: "the foundation",
      cta: "See pricing, one-off services and terms",
    },
    process: {
      title: "Getting started is simple",
      subtitle: "Three steps, always knowing what we're doing and why.",
      steps: [
        { title: "Free audit", desc: "You message me on WhatsApp. I review your profile and your business and tell you what's holding your bookings back. No strings attached." },
        { title: "Your tailored plan", desc: "I present the diagnosis and the plan that makes sense for you: pillars, calendar and content with a clear purpose." },
        { title: "Execution and improvement", desc: "We publish, look after your community and measure every month to adjust and keep improving." },
      ],
    },
    roi: {
      title: "How many bookings do you need for the plan to pay for itself?",
      lead: "Run the numbers with what you actually charge.",
      ticketLabel: "Your average price per appointment (€)",
      planLabel: "Plan",
      resultOne: "With {count} new booking a month, the {plan} plan pays for itself.",
      resultMany: "With {count} new bookings a month, the {plan} plan pays for itself.",
      note: "This is a rough calculation, not a promise of results: it depends on your business, your offer and your consistency.",
    },
    about: {
      quote: "I don't believe in posting for the sake of posting. I believe in building brands that connect and grow sustainably.",
      signature: "Jenifer Roelstraete, founder of Lucentia Social",
    },
    resources: {
      title: "Free resources for your business",
      lead: "Downloadable guides to optimise your profile and see how Instagram, your website and bookings work together.",
      cta: "See all resources",
    },
    contact: {
      title: "Contact",
      lead: "Tell me what your business needs. I reply within 24 hours, Monday to Friday.",
      name: "Name",
      email: "Email",
      message: "Message",
      messagePlaceholder: "Tell me about your business and how I can help.",
      submit: "Send message",
      success: "Thank you. I’ll reply as soon as I can, usually within 24 hours.",
      error: "The message could not be sent. Try again or write on WhatsApp.",
      validation: "Check the name, a valid email and the message.",
      privacySuffix: "and I authorise Lucentia Social to use this information to reply to my enquiry.",
    },
    final: {
      title: "Shall we build a brand that sells with strategy?",
      body: "Start with the free audit: message me on WhatsApp, I'll review your profile and your business and tell you honestly what makes sense for you. If you'd rather learn to do it yourself, book the Strategic Advisory.",
    },
  },
  services: [
    {
      title: "Strategic Instagram Management",
      desc: "We turn your profile into a tool that attracts clients, builds trust and increases your bookings.",
      icon: Instagram,
      iconTone: "primary",
      features: [
        "Personalised strategy",
        "Content that connects",
        "Results that generate bookings",
      ],
      rung: "Delegate",
      href: "/gestion-instagram",
      ctaLabel: "Learn more",
    },
    {
      title: "Strategic Instagram Advisory",
      desc: "Learn how to manage your business Instagram with a 100% personalised strategy and a video guide you can follow at your own pace.",
      icon: Sparkles,
      iconTone: "primary",
      features: [
        "Personalised video advisory",
        "Lucentia Implementation Kit",
        "7 days of WhatsApp support",
      ],
      rung: "Learn",
      href: "/asesoria-instagram",
      ctaLabel: "Learn more",
    },
    {
      title: "Strategic Website",
      desc: "Your website shouldn’t just look good. It should turn visits into clients and bookings.",
      icon: Globe,
      iconTone: "primary",
      features: [
        "Custom design",
        "Booking system",
        "Mobile optimised",
      ],
      rung: "Convert",
      href: "/pagina-web",
      ctaLabel: "Learn more",
    },
  ],
  about: {
    eyebrow: "About me",
    title: "Hi, I'm Jenifer, founder of Lucentia Social.",
    paragraphs: [
      "I believe a good strategy starts long before posting on Instagram.",
      "It starts by understanding the business: what it offers, what makes it different, who it wants to reach and, above all, what it needs in order to grow.",
      "That’s why I created Lucentia Social: to help businesses stop doing marketing without direction and start building a digital presence that actually serves their goals.",
      "I get involved in every project to understand how the business works and find what can be improved, simplified or amplified.",
      "Because I don’t believe in formulas that work the same for everyone.",
      "Every business has a different story, audience and goal. And the strategy should start from there.",
    ],
    expectTitle: "What can you expect when working with me?",
    expectIntro:
      "A strategic perspective, clear communication and solutions designed for your business — not a package of actions done on autopilot.",
    highlights: [
      {
        title: "A strategy designed for your business.",
        description:
          "We don’t apply generic formulas. First we understand where you are and what you want to achieve.",
      },
      {
        title: "A perspective that connects the pieces.",
        description:
          "Instagram, website, content and processes need to work together, not as isolated elements.",
      },
      {
        title: "Close support and clear communication.",
        description:
          "You’ll know what we’re doing, why we’re doing it and where we’re heading.",
      },
      {
        title: "Decisions with intention.",
        description:
          "Every action has a purpose: attract, connect, convert or improve.",
      },
    ],
    ctaLabel: "Let's work together",
  },
  methodology: {
    eyebrow: "Lucentia System®",
    title: "Your business needs a system, not isolated tools",
    subtitle:
      "Every piece has a role: attract clients, turn visits into bookings and grow your business in an organised way.",
    ctaLabel: "Discover the Lucentia System®",
    steps: [
      {
        title: "Instagram",
        desc: "We attract the attention of your ideal client.",
        icon: Instagram,
      },
      {
        title: "Website",
        desc: "We turn visits into opportunities.",
        icon: Monitor,
      },
      {
        title: "Booking system",
        desc: "We make it easy for them to book anytime.",
        icon: Calendar,
      },
      {
        title: "Automation",
        desc: "We make your business work even when you’re offline.",
        icon: Settings,
      },
      {
        title: "Continuous optimisation",
        desc: "We measure, analyse and improve so your business keeps growing.",
        icon: BarChart3,
      },
    ],
    resultsTitle: "When all these pieces work together...",
    results: [
      "More bookings.",
      "Less time answering messages.",
      "Organised processes.",
      "A better experience for your clients.",
      "A business ready to grow.",
    ],
  },
  methodPage: {
    hero: {
      title: "What is the Lucentia System®?",
      subtitle:
        "It’s a strategic system designed to help your business get more bookings in an organised, automatic and consistent way.",
      highlightLead: "It’s not just about posting on Instagram.",
      highlightBody: "It’s about creating a system that grows your business.",
    },
    why: {
      title: "Why we created a system",
      problems: [
        "Many businesses post on Instagram… but don’t have a strategy.",
        "Others have a website… but it doesn’t generate bookings.",
        "And many spend all day answering WhatsApp.",
      ],
      conclusion:
        "That’s why we created a system where every part has a role.",
    },
    flow: {
      title: "How it works",
      items: [
        {
          title: "Instagram",
          desc: "We attract the attention of your ideal client.",
        },
        {
          title: "Website",
          desc: "We turn visits into opportunities.",
        },
        {
          title: "Booking system",
          desc: "We make it easy for them to book anytime.",
        },
        {
          title: "Automation",
          desc: "We make your business work even when you’re offline.",
        },
        {
          title: "Continuous optimisation",
          desc: "We measure, analyse and improve so your business keeps growing.",
        },
      ],
      summary:
        "The Lucentia System® combines strategy, acquisition, conversion and optimisation so every piece of your business works together.",
    },
    process: {
      title: "How do we implement it?",
      subtitle: "We do it with the Lucentia Method",
      stepLabel: "Step",
      steps: [
        {
          title: "We discover",
          subtitle: "Strategic analysis",
          description:
            "First we get to know your business. We don’t apply generic solutions. Before designing any strategy, we analyse how your business works, what’s holding back growth and where the opportunities are.",
          listTitle: "What do we analyse?",
          items: [
            "Strategic business audit",
            "SWOT analysis",
            "Instagram profile diagnosis and optimisation",
            "Digital presence analysis",
            "Clear goal definition",
            "Ideal audience",
            "Competition",
          ],
          footer:
            "First we understand. Then we design a strategy that actually works.",
        },
        {
          title: "We design",
          description:
            "With all the information gathered, we design a personalised strategy.",
          listTitle: "We define:",
          items: [
            "Positioning",
            "Content",
            "Profile",
            "Funnel",
            "Goals",
          ],
          footer:
            "We design with purpose. Every decision has one goal: grow your business.",
        },
        {
          title: "We implement",
          description: "It’s time to put everything in motion.",
          items: [
            "Instagram management.",
            "Web design.",
            "Booking system.",
            "Client follow-up.",
          ],
          footer: "We turn strategy into real actions.",
        },
        {
          title: "We automate",
          description:
            "We create processes that keep working even when you’re not working.",
          items: [
            "Online bookings",
            "Automatic reminders",
            "Client follow-up",
            "Automations",
            "Calendar organisation",
          ],
          footer: "Fewer repetitive tasks. More time for you.",
        },
        {
          title: "We optimise",
          description: "We measure results and improve continuously.",
          items: [
            "We analyse metrics.",
            "We adjust the strategy.",
            "We look for new opportunities.",
          ],
          footer: "Because growth is a continuous process.",
        },
      ],
    },
    results: {
      title: "What do you get with the Lucentia Method®?",
      items: [
        "More bookings.",
        "An organised business.",
        "Less time answering messages.",
        "Automated processes.",
        "A clear strategy.",
        "A system built to grow.",
      ],
      tagline: "One system. One method. Real results.",
    },
    cta: {
      title: "Ready to take the first step?",
      subtitle: "All great growth starts with a good diagnosis.",
      alternativeA: "Your business can keep depending on day-to-day hustle…",
      alternativeB:
        "Or it can start running with a strategy, a system and processes that work for you.",
      description:
        "Request a free Strategic Audit and discover what your business needs to attract more clients, get more bookings and grow with a system designed for you.",
      closing: "Find out if the Lucentia System® is what your business needs.",
      buttonLabel: "Request a free strategic audit",
      tagline: "Lucentia System® — Strategy · System · Results",
    },
  },
  webServicePage: {
    hero: {
      eyebrow: "Design of a",
      title: "Strategic",
      titleAccent: "Website",
      subtitle:
        "Your website shouldn’t just look good. It should turn visits into clients and bookings.",
      highlights: [
        "Custom design",
        "Booking system",
        "Mobile optimised",
      ],
      closing:
        "We design websites that work for your business, while you focus on what matters most: your clients.",
      pillars: "Strategy · Design · System · Results",
    },
    includes: {
      title: "What’s included in your website?",
      items: [
        {
          title: "100% custom design",
          desc: "We create a unique website that reflects the essence of your brand.",
        },
        {
          title: "Adapted for mobiles, tablets and desktop",
          desc: "A great experience on any device.",
        },
        {
          title: "Online booking system",
          desc: "Your clients book 24/7 quickly and easily.",
          note: "if your business needs it",
        },
        {
          title: "Payment gateway",
          desc: "Charge for bookings, deposits or sell products securely.",
          note: "if your business needs it",
        },
        {
          title: "AI assistant",
          desc: "Answers frequent questions and improves automated support.",
        },
        {
          title: "Direct WhatsApp button",
          desc: "Immediate contact with your clients in one click.",
        },
        {
          title: "Google Maps integration",
          desc: "Your location always visible so clients can find you easily.",
        },
        {
          title: "Image or product gallery",
          desc: "Show the best of your business with images that convert.",
          note: "if it applies to your activity",
        },
        {
          title: "Google reviews",
          desc: "Show your clients’ trust directly on your website.",
        },
        {
          title: "Contact form",
          desc: "Makes enquiries, quotes and new contacts easier.",
        },
      ],
      summary:
        "Everything your business needs in one place — for a professional experience and more conversions.",
    },
    optimization: {
      eyebrow: "We optimise",
      title: "Your website so people find you, choose you and book with you.",
      subtitle:
        "We apply the best optimisation strategies so your business gains visibility, authority and converts more.",
      items: [
        {
          title: "SEO optimisation",
          desc: "To improve your ranking on Google.",
        },
        {
          title: "AEO optimisation",
          desc: "So your business can appear as an answer to frequent questions in search engines and assistants.",
        },
        {
          title: "GEO optimisation",
          desc: "To prepare your website for new AI-powered search engines.",
        },
        {
          title: "Google Business Profile setup",
          desc: "So your business stands out on Google and attracts more local clients.",
        },
        {
          title: "Google Analytics",
          desc: "We measure, analyse and optimise every action so your website keeps improving.",
        },
        {
          title: "Conversion-focused design",
          desc: "Every section of your website is designed to turn visits into clients and bookings.",
        },
      ],
      calloutLines: ["More visibility.", "More trust.", "More results."],
      calloutBody:
        "Strategy, technology and data working for your growth.",
    },
    audience: {
      title: "Ideal for businesses that work with bookings",
      items: [
        "Hair salons",
        "Beauty centres",
        "Tattoo and piercing studios",
        "Physiotherapists",
        "Nutritionists",
        "Clinics",
        "Photographers",
      ],
    },
    cta: {
      philosophy:
        "We believe in brands with purpose, and in projects that grow with strategy.",
      description:
        "Message me the word WEB and I’ll send you all the information about the service, the work process and how we can help your business grow.",
      buttonLabel: "Message WEB on WhatsApp",
      whatsappMessage: "WEB",
      pillars: [
        { title: "Strategy that guides", desc: "Clarity to decide." },
        { title: "Design that connects", desc: "A website that builds trust." },
        {
          title: "A system that works for you",
          desc: "Bookings and automated processes.",
        },
        {
          title: "Results that grow your brand",
          desc: "More clients and more bookings.",
        },
      ],
    },
  },
  instagramManagementPage: {
    hero: {
      title: "Strategic",
      titleAccent: "Instagram Management",
      subtitle:
        "We turn your profile into a tool that attracts clients, builds trust and increases your bookings.",
      highlights: [
        "Personalised strategy",
        "Content that connects",
        "A community that trusts you",
        "Results that generate bookings",
      ],
      closing:
        "We take care of growing your business while you get back time for what matters most: your clients.",
    },
    includes: {
      title: "What’s included in our service?",
      items: [
        {
          title: "Personalised strategy",
          desc: "A plan tailored to your business and ideal client.",
        },
        {
          title: "Monthly planning",
          desc: "A content calendar aligned with your goals.",
        },
        {
          title: "Post design",
          desc: "Professional visuals that reflect your brand.",
        },
        {
          title: "Strategic copywriting",
          desc: "Copy that connects, persuades and drives action.",
        },
        {
          title: "Content scheduling",
          desc: "Posts published at the ideal time for your audience.",
        },
        {
          title: "Community management",
          desc: "Replies to messages and comments to strengthen relationships with your audience.",
        },
        {
          title: "Metrics analysis",
          desc: "We measure results to know what works and what to improve.",
        },
        {
          title: "Continuous optimisation",
          desc: "We adjust the strategy every month so you keep growing.",
        },
        {
          title: "Profile optimisation",
          desc: "We improve your profile to build trust and increase conversions.",
        },
        {
          title: "Strategic advisory",
          desc: "I support you with ideas and decisions that grow your business.",
        },
      ],
      summary:
        "We create the strategy — you focus on giving your clients the best experience.",
      pillars: "Strategy · System · Results",
    },
    pitch: {
      eyebrow: "Strategic Instagram Management",
      titleBefore: "Instagram",
      titleAccent: "that works",
      titleAfter: "to fill your calendar",
      subtitle:
        "We create strategies that attract the right people, build trust and turn them into loyal clients.",
    },
    audience: {
      title: "Ideal for businesses that work with bookings",
      items: [
        "Hair salons",
        "Beauty centres",
        "Tattoo and piercing studios",
        "Physiotherapists",
        "Nutritionists",
        "Clinics",
        "Photographers",
        "Dog grooming",
        "Nail salons",
      ],
      note: "And any business that works with bookings.",
    },
    method: {
      title: "We work with the Lucentia System®",
      subtitle:
        "Strategy, system and real results for sustainable growth.",
      steps: [
        "We discover",
        "We design",
        "We implement",
        "We automate",
        "We optimise",
      ],
      href: "/metodo",
      ctaLabel: "Discover the Lucentia System®",
    },
    cta: {
      description:
        "Message me the word INSTAGRAM and I’ll send you all the information about the service, how we work and the ideal plan for your business.",
      buttonLabel: "Message INSTAGRAM on WhatsApp",
      whatsappMessage: "INSTAGRAM",
    },
  },
  instagramAdvisoryPage: {
    hero: {
      title: "Strategic",
      titleAccent: "Instagram Advisory",
      subtitle:
        "Learn how to manage your business Instagram with a personalised strategy.",
      paragraphs: [
        "Get a 100% personalised advisory, adapted exclusively to your brand.",
        "Once I’ve analysed your business, I’ll record a complete explanation for you. You’ll receive a private YouTube link so you can watch it as many times as you need and move at your own pace.",
      ],
      videoNote:
        "You’ll receive a private YouTube link with your personalised advisory so you can watch it as many times as you need and move at your own pace.",
      pillars: "Strategy that connects. Systems that generate results.",
    },
    includes: {
      title: "What’s included?",
      groups: [
        {
          title: "Complete profile optimisation",
          items: [
            "Strategic bio.",
            "Profile organisation.",
            "Links and setup.",
          ],
        },
        {
          title: "Strategic highlight Stories",
          items: [
            "Custom cover design.",
            "Story organisation to guide visitors toward purchase or booking.",
          ],
        },
        {
          title: "Ideal client definition",
          items: [
            "Who you’re speaking to.",
            "How to communicate with that person.",
            "What content they need to see.",
          ],
        },
        {
          title: "Stats analysis",
          items: [
            "How to interpret metrics.",
            "Which data to analyse.",
            "How to use them to make better decisions.",
          ],
        },
        {
          title: "Content strategy",
          items: [
            "Content pillars.",
            "Post ideas.",
            "Recommended frequency.",
          ],
        },
        {
          title: "Stories and hashtags",
          items: [
            "How to create Stories that build connection.",
            "Hashtag strategy to grow your reach.",
          ],
        },
      ],
    },
    kit: {
      title: "When your advisory ends, you also receive this kit to put it into practice.",
      kitTitle: "Lucentia Implementation Kit",
      items: [
        {
          title: "Profile Optimisation Guide",
          desc: "A practical guide so you always know how to optimise your profile, what information to include and how to organise it strategically to build trust and turn visitors into clients.",
        },
        {
          title: "7 personalised content ideas for your brand",
          desc: "You’ll receive a content plan created exclusively for your business, based on your services, goals and the type of client you want to attract.",
          details: [
            "Format (Reel or Carousel).",
            "Content pillar.",
            "Post objective.",
            "Copywriting ready to adapt.",
            "Call to action (CTA).",
          ],
        },
      ],
      summary:
        "Everything designed so you can create, edit and publish your content with a clear strategy adapted to your brand.",
      noteTitle: "Important",
      note: "This advisory includes the strategy and personalised ideas for your business. It does not include post design. The goal is for you to have a clear guide to create, edit and publish content with your brand’s visual identity — and reuse it whenever you need.",
    },
    support: {
      title: "7 days of WhatsApp support for questions",
      description:
        "For 7 days after receiving your advisory, you can message me on WhatsApp to resolve any questions about implementing the strategy while you start applying it.",
      highlight:
        "Real support so you’re not alone in the process. Your growth is my priority.",
    },
    cta: {
      title: "Book your Strategic Advisory",
      description:
        "Answer a few questions about your business so I can study your account, then pick a day and time on the calendar. If you'd rather start with a free diagnosis, request the free audit on WhatsApp.",
      buttonLabel: "Book strategic advisory",
      whatsappMessage: "Hi Jenifer, I'd like the free audit.",
    },
  },
  audit: {
    eyebrow: "Free strategic audit",
    title: "Discover what opportunities your business has to get more bookings",
    description:
      "Request your free Strategic Audit and receive a personalised diagnosis with concrete opportunities to grow.",
    ctaLabel: "Request the free audit on WhatsApp",
    whatsappLabel: "Free audit",
  },
  resourcesPage: {
    title: "Resources & guides",
    description: "Free resources to grow your business.",
  },
};
